import {
  addDays,
  addHours,
  addSeconds,
  addWeeks,
  endOfDay,
  format,
  getISOWeek,
  getISOWeekYear,
  getMonth,
  getYear,
  isSameDay,
  isSameWeek,
  isValid,
  isWithinRange,
  startOfDay,
  subDays,
  subHours,
  subSeconds
} from '@awork/_shared/functions/date-fns-wrappers'
import { stringToTime } from '@awork/_shared/functions/time-operations'
import type { Time } from '@angular/common'
import type { Translations } from '@awork/i18n/translations'
import type { ISOLanguage } from '@awork/_shared/models/account.model'
import { fromZonedTime } from 'date-fns-tz'
import { UTCDate } from '@date-fns/utc'
import { calendarDate } from './calendar-date'

const timezoneFormatters = new Map<string, Intl.DateTimeFormat>()

// Validation is independent of the date. Keep offsets in date-fns-tz so DST still applies.
const validatedTimezones = new Set<string>()

export enum DateCategories {
  Now = 'NOW',
  WithinLastHour = 'WITHIN_LAST_HOUR',
  Today = 'TODAY',
  Yesterday = 'YESTERDAY',
  LastWeek = 'LAST_WEEK',
  FiveDaysAgo = 'FIVE_DAYS_AGO',
  WithinAnHour = 'WITHIN_AN_HOUR',
  Tomorrow = 'TOMORROW',
  InFiveDays = 'IN_FIVE_DAYS',
  None = 'NONE'
}

export interface DateRange {
  startDate: Date
  endDate: Date
}

/**
 * Converts a Date or string to a timestamp (milliseconds since epoch).
 * Useful for sorting or comparing dates when the type is Date | string.
 * @param {Date | string} date The date to convert
 * @returns {number} The timestamp in milliseconds
 */
export function toTime(date: Date | string): number {
  return (date instanceof Date ? date : new Date(date)).getTime()
}

/**
 * Converts a Date or string to a Date object.
 * @param {Date | string} date The date to convert
 * @returns {Date} Date object
 */
export function toDate(date: Date | string): Date {
  return date instanceof Date ? date : new Date(date)
}

/**
 * Converts a date to 'YYYY-MM-DD' string
 * @param {Date | string} date
 * @param {boolean} useUTC If true, it will use UTC date values
 * @returns {string}
 */
export function dateToISO(date: Date | string, useUTC?: boolean): string {
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date
  }

  let newDate: Date

  if (date && !(date instanceof Date)) {
    newDate = new Date(date)
  } else {
    newDate = date as Date
  }

  if (!newDate) {
    return null
  }

  if (useUTC) {
    const year = newDate.getUTCFullYear()
    const month = String(newDate.getUTCMonth() + 1).padStart(2, '0')
    const date = String(newDate.getUTCDate()).padStart(2, '0')

    return `${year}-${month}-${date}`
  }

  return format(newDate, 'yyyy-MM-dd')
}

/**
 * Parses an ISO timestamp or a date with optional time, using the selected month order.
 * @param {string} dateStr
 * @param {boolean} isMonthFirst
 * @returns {Date}
 */
export function stringToDate(dateStr: string, isMonthFirst = false): Date {
  if (dateStr.trim().includes('T')) {
    return Date.parse(dateStr) ? new Date(dateStr) : null
  }

  let date: Date
  let separator = '/'

  // Normalize string
  dateStr = dateStr.trim().replace(',', '')

  // Get what is after the first white space
  const timeString = dateStr.includes(' ') ? dateStr.substr(dateStr.indexOf(' ') + 1) : null
  let time: Time

  if (dateStr.includes('-')) {
    separator = '-'
  } else if (dateStr.includes('.')) {
    separator = '.'
  }

  if (timeString) {
    time = stringToTime(timeString)
  }

  const inputDate = dateStr
    .trim()
    .substring(0, dateStr.indexOf(' ') > 0 ? dateStr.indexOf(' ') : undefined)
    .split(separator)

  // Remove last item if empty (dateStr ended with separator)
  if (!inputDate[inputDate.length - 1]) {
    inputDate.pop()
  }

  if (inputDate?.length > 1) {
    // If input length === 2, it means the year is not included, so by default here we add current year as default
    if (inputDate.length === 2) {
      inputDate[2] = `${new Date().getFullYear()}`
    }

    const yearInput = Number(inputDate[2])
    const year = inputDate[2].length === 2 ? getFullYearFromTwoDigits(yearInput) : yearInput
    const month = isMonthFirst ? Number(inputDate[0]) - 1 : Number(inputDate[1]) - 1
    const day = isMonthFirst ? Number(inputDate[1]) : Number(inputDate[0])

    date = new Date(year, month, day, time ? time.hours : null, time ? time.minutes : null)
  }

  return date && !isNaN(date.getTime()) ? date : null
}

/**
 * Formats a week relative to today, or as its week number and year.
 * @param {Date | string} date
 * @param {Translations['en'] & { currentLang: ISOLanguage }} translations
 * @returns {string}
 */
export function getWeekYearString(
  date: Date | string,
  translations: Translations['en'] & { currentLang: ISOLanguage }
): string {
  const today = new Date()

  if (typeof date === 'string') {
    date = new Date(date)
  }

  if (isSameWeek(date, today)) {
    return translations.dates.thisWeek
  }

  if (isSameWeek(date, addWeeks(today, -1))) {
    return translations.dates.lastWeek
  }

  const week = getISOWeek(date)
  const year = getISOWeekYear(date)

  // Don't include year if date is from the current ISO week year
  if (year === getISOWeekYear(today)) {
    return `${translations.dates.calendarWeekShort} ${week}`
  }

  return `${translations.dates.calendarWeekShort} ${week} ${year}`
}

/**
 * Formats a date with the translated month name and year.
 * @param {Date | string} date
 * @param {Translations['en'] & { currentLang: ISOLanguage }} translations
 * @returns {string}
 */
export function getMonthYearString(
  date: Date | string,
  translations: Translations['en'] & { currentLang: ISOLanguage }
): string {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  const translatedMonthArray = [
    translations.DateSelectorComponent.january,
    translations.DateSelectorComponent.february,
    translations.DateSelectorComponent.march,
    translations.DateSelectorComponent.april,
    translations.DateSelectorComponent.may,
    translations.DateSelectorComponent.june,
    translations.DateSelectorComponent.july,
    translations.DateSelectorComponent.august,
    translations.DateSelectorComponent.september,
    translations.DateSelectorComponent.october,
    translations.DateSelectorComponent.november,
    translations.DateSelectorComponent.december
  ]

  const month = getMonth(date)
  const year = getYear(date)

  return `${translatedMonthArray[month]}, ${year}`
}

/**
 * Returns the date category were the date belongs
 * @param {Date | string} value
 * @param {Date} [now=new Date()]
 * @returns {DateCategories}
 */
export function getDateCategory(value: Date | string, now = new Date()): DateCategories {
  if (value === 'today') {
    value = now
  }

  const dateValue = value instanceof Date ? value : new Date(value)

  const isEarlierNow = isWithinRange(dateValue, subSeconds(now, 60), now)

  if (isEarlierNow) {
    return DateCategories.Now
  }

  const isWithinLastHour = isWithinRange(dateValue, subHours(now, 1), now)

  if (isWithinLastHour) {
    return DateCategories.WithinLastHour
  }

  const isEarlierToday = isWithinRange(dateValue, startOfDay(now), now)

  if (isEarlierToday) {
    return DateCategories.Today
  }

  if (isSameDay(dateValue, subDays(now, 1))) {
    return DateCategories.Yesterday
  }

  const is5DaysAgo = isWithinRange(dateValue, subDays(now, 5), now)

  if (is5DaysAgo) {
    return DateCategories.FiveDaysAgo
  }

  // TODO: maybe we should use difference in calendar weeks?
  const isLastWeek = isSameWeek(dateValue, addWeeks(now, -1))

  if (isLastWeek) {
    return DateCategories.LastWeek
  }

  const isLaterNow = isWithinRange(dateValue, now, addSeconds(now, 60))

  if (isLaterNow) {
    return DateCategories.Now
  }

  const isWithinHour = isWithinRange(dateValue, now, addHours(now, 1))

  if (isWithinHour) {
    return DateCategories.WithinAnHour
  }

  const isLaterToday = isWithinRange(dateValue, now, endOfDay(now))

  if (isLaterToday) {
    return DateCategories.Today
  }

  if (isSameDay(dateValue, addDays(now, 1))) {
    return DateCategories.Tomorrow
  }

  const isIn5Days = isWithinRange(dateValue, now, addDays(now, 5))

  if (isIn5Days) {
    return DateCategories.InFiveDays
  }

  return DateCategories.None
}

/**
 * Returns the date with the time adjusted to 00:00 of the local timezone
 * This is useful when we need only the date (without time), in the local
 * timezone as T:00:00 from the original utc date (for example for birthdates)
 * @param {Date | string} date
 * @returns {Date}
 */
export function adjustDateToLocalDateStart(date: Date | string): Date {
  if (!(date instanceof Date)) {
    date = new Date(date)
  } else {
    date = calendarDate(date) // Keep calendar fields when copying the date.
  }

  // Sets the utc date to 00:00:00
  date.setUTCHours(0, 0, 0, 0)

  // Applies the timezone offset so the local representation stays at 00:00:00
  const timezoneOffset = date.getTimezoneOffset() * 60000

  date.setTime(date.getTime() + timezoneOffset)

  return date
}

/**
 * Transform a date to YYYYMMDD number
 * @param {Date | string} date
 * @returns {number}
 */
export function dateToNumeric(date: Date | string): number {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }

  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')

  return Number.parseInt(`${year}${month}${day}`)
}

/**
 * Check if a date is an instance of Date
 * @param {Date | string} date
 * @returns {date is Date}
 */
export function isInstanceOfDate(date: Date | string): date is Date {
  // Also true for UTCDate when the global Date is replaced, for example by fake timers
  return Object.prototype.toString.call(date) === '[object Date]'
}

/**
 * Checks if a date is within two dates ignoring the times
 * @param {string | Date} date
 * @param {Date} start
 * @param {Date} end
 * @returns {boolean}
 */
export function isDateWithinRange(date: string | Date, start: Date, end: Date): boolean {
  const dayToCheck = isInstanceOfDate(date) ? date : new Date(date)

  const numericDay = dateToNumeric(dayToCheck)
  const numericStart = dateToNumeric(start)
  const numericEnd = dateToNumeric(end)

  return numericDay >= numericStart && numericDay <= numericEnd
}

/**
 * Checks if two date ranges overlap ignoring the times
 * @param {{ start: Date; end: Date; }} rangeA
 * @param {{ start: Date; end: Date; }} rangeB
 * @param rangeA.start
 * @param rangeA.end
 * @param rangeB.start
 * @param rangeB.end
 * @returns {boolean}
 */
export function areDatesOverlapping(rangeA: { start: Date; end: Date }, rangeB: { start: Date; end: Date }): boolean {
  const rangeAStart = dateToNumeric(rangeA.start)
  const rangeAEnd = dateToNumeric(rangeA.end)

  const rangeBStart = dateToNumeric(rangeB.start)
  const rangeBEnd = dateToNumeric(rangeB.end)

  return rangeAStart <= rangeBEnd && rangeAEnd >= rangeBStart
}

/**
 * Return local date without time from UTC string
 * @param {Date | string} utcString
 * @returns {Date}
 */
export function getLocalDateFromUTCString(utcString: Date | string): Date {
  if (utcString instanceof Date) {
    return utcString
  }

  const day = new Date(utcString).getUTCDate()
  const month = new Date(utcString).getUTCMonth()
  const year = new Date(utcString).getUTCFullYear()

  return new Date(year, month, day)
}

/**
 * Returns segments of the difference between two dates given a segment size
 * @param {Date} start
 * @param {Date} end
 * @param {number} segmentSize in days
 * @returns {DateRange[]}
 */
export function getRangeSegments(start: Date, end: Date, segmentSize: number): DateRange[] {
  const endDate = new Date(end)

  endDate.setUTCHours(23, 59, 59)

  let startDate = new Date(start)

  startDate.setUTCHours(0, 0, 0, 0)

  let segmentEndDate = addDays(startDate, segmentSize - 1)

  segmentEndDate.setUTCHours(23, 59, 59)

  const dateSegments = []

  while (dateToNumeric(segmentEndDate) < dateToNumeric(endDate)) {
    dateSegments.push({ startDate, endDate: segmentEndDate })

    startDate = addDays(segmentEndDate, 1)
    segmentEndDate = addDays(startDate, segmentSize - 1)
    startDate.setUTCHours(0, 0, 0, 0)
  }

  const lastEndDate = dateSegments[dateSegments.length - 1]?.endDate
  const hasRemainingDays =
    dateToNumeric(startDate) < dateToNumeric(endDate) ||
    (lastEndDate && dateToNumeric(lastEndDate) < dateToNumeric(endDate))

  if (hasRemainingDays) {
    endDate.setUTCHours(23, 59, 59)
    dateSegments.push({ startDate, endDate })
  }

  return dateSegments
}

/**
 * Calculates the full year from two digits utilizing a "windowing" technique
 * of max 100 years (https://www.uic.edu/depts/accc/software/isodates/fixing.html)
 * Ex: if today is 2022, any value lower than 42 is 2000s otherwise 1900s
 * @param {number} input
 * @param {number} year
 * @returns {number}
 */
export function getFullYearFromTwoDigits(input: number, year?: number): number {
  if (!year) {
    year = new Date().getFullYear()
  }

  const window = 80
  const minYear = year - window

  let result = input + 1900

  if (result < minYear) {
    result += 100
  }

  return result
}

/**
 * Converts a given date or date string to a UTC date object.
 * @param {Date | string} date
 * @param {string} timezone
 * @returns {Date}
 * @private
 */
export function convertToUTC(date: Date | string, timezone: string): Date {
  // Pass clock fields as text: date-fns-tz clones Date inputs into browser-local Dates.
  const clock = typeof date === 'string' ? date : format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS")
  return fromZonedTime(clock, getValidTimezone(timezone))
}

/**
 * Converts an instant to calendar fields in the specified timezone.
 * The returned UTCDate stores clock fields, not an instant. Use convertToUTC before saving it.
 * @param {Date | string} date The date to be converted. Can be a Date object or a string.
 * @param {string} timezone The target timezone for the conversion (e.g., 'America/New_York').
 * @param {true} returnString If true, returns the date as a formatted string. Otherwise, returns a Date object.
 * @returns {string} The converted date. Returns a formatted string if `returnString` is true, otherwise a Date object.
 */
export function convertToTimezone(date: Date | string, timezone: string, returnString: true): string
export function convertToTimezone(date: Date | string, timezone: string, returnString?: false): Date
export function convertToTimezone(date: Date | string, timezone: string, returnString = false): string | Date {
  if (!isInstanceOfDate(date)) {
    date = new Date(date)
  }

  if (!isValid(date)) {
    date = new Date()
  }

  const validTimezone = getValidTimezone(timezone)
  let formatter = timezoneFormatters.get(validTimezone)

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: validTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    })
    timezoneFormatters.set(validTimezone, formatter)
  }

  const parts = formatter.formatToParts(date)
  const field = (name: Intl.DateTimeFormatPartTypes): number => Number(parts.find(part => part.type === name)?.value)
  const zonedDate = new UTCDate(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour'),
    field('minute'),
    field('second'),
    date.getMilliseconds()
  )

  return returnString ? format(zonedDate, 'yyyy-MM-dd HH:mm:ss') : zonedDate
}

/**
 * Returns a valid timezone string from the given timezone string.
 * If the given timezone is invalid, returns 'UTC'.
 * @param {string} timezone The timezone string to validate.
 * @returns {string} The valid timezone string.
 */
function getValidTimezone(timezone: string): string {
  if (!timezone) {
    return 'UTC'
  }

  if (validatedTimezones.has(timezone)) {
    return timezone
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date(0))
    validatedTimezones.add(timezone)

    return timezone
  } catch {
    return 'UTC'
  }
}

/**
 * Converts a time string in the format "HH:MM:SS" to an object with hours, minutes, and seconds as numbers.
 * @param {string} timeStr The time string to convert.
 * @returns {{ hours: number; minutes: number; seconds: number; } | null} An object containing the hours, minutes, and seconds as numbers.
 */
export function parseTimeString(timeStr: string): { hours: number; minutes: number; seconds: number } | null {
  if (!timeStr || typeof timeStr !== 'string') {
    return null
  }

  const [hours, minutes, seconds] = timeStr.split(':').map(Number)

  return { hours, minutes, seconds }
}

/**
 * Unifies a time string and a date into a Date object.
 * @param {string} timeStr The time string to be unified.
 * @param {string | Date} date The date to be unified. Can be a string or a Date object.
 * @returns {Date | null} The unified date and time as Date, or null if the time string is invalid.
 */
export function unifyTimeAndDate(timeStr: string, date: string | Date): Date | null {
  const timeObj = parseTimeString(timeStr)

  if (!timeObj) {
    return null
  }

  const newDate = calendarDate(date)

  newDate.setHours(timeObj.hours)
  newDate.setMinutes(timeObj.minutes)
  newDate.setSeconds(timeObj.seconds)

  return newDate
}

/**
 * Parses a date-only string or ISO datetime string to a local Date object.
 * Extracts only the date portion, ignoring any time component.
 *
 * Supported formats:
 * - 'YYYY-MM-DD' (date-only)
 * - 'YYYY-MM-DDTHH:mm:ss' (ISO datetime without timezone)
 * - 'YYYY-MM-DDTHH:mm:ssZ' (ISO datetime with Z suffix)
 * @param {string} dateString Date string to parse
 * @returns {Date} Local date object (at midnight local time)
 */
export function parseDateOnlyString(dateString: string): Date {
  // Extract just the date part (first 10 characters: YYYY-MM-DD)
  const datePart = dateString.substring(0, 10)
  const [year, month, day] = datePart.split('-').map(Number)

  return new Date(year, month - 1, day)
}

/**
 * Extracts the date portion from a Date or string value.
 * Returns 'YYYY-MM-DD' format string.
 *
 * Strategy:
 * - For Date objects: extract local year/month/day (user's intended date)
 * - For ISO strings with time component: extract UTC date, discard time
 * - For date-only strings 'YYYY-MM-DD': return as-is
 * @param {Date | string} date The date to extract
 * @returns {string | null} Date string in 'YYYY-MM-DD' format
 */
export function extractDateOnlyISOString(date: Date | string): string | null {
  if (!date) {
    return null
  }

  if (date instanceof Date) {
    return format(date, 'yyyy-MM-dd')
  }

  if (typeof date === 'string') {
    // For strings with time component, extract UTC date
    if (date.includes('T') || date.includes('Z') || date.includes('+') || date.includes(' ')) {
      const d = new Date(date)
      const year = d.getUTCFullYear()
      const month = String(d.getUTCMonth() + 1).padStart(2, '0')
      const day = String(d.getUTCDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    }

    // Already date-only string, return as-is
    return date
  }

  return null
}
